import PropTypes from 'prop-types';
import React, { Component } from 'react';
import _ from 'lodash';
import { connect } from 'react-redux';
import Toggle from 'material-ui/Toggle';
import { List, ListItem } from 'material-ui/List';
import { Dialog, FlatButton } from 'material-ui';

import ActionExitToApp from 'material-ui/svg-icons/action/exit-to-app';
import ActionAccountBox from 'material-ui/svg-icons/action/account-box';
import ActionBugReport from 'material-ui/svg-icons/action/bug-report';
import ActionHelp from 'material-ui/svg-icons/action/help';
import ActionHome from 'material-ui/svg-icons/action/home';
import ActionInfo from 'material-ui/svg-icons/action/info';
import ActionLanguage from 'material-ui/svg-icons/action/language';
import CommunicationLiveHelp from 'material-ui/svg-icons/communication/live-help';
import CommunicationEmail from 'material-ui/svg-icons/communication/email';

import menuAccess from '../../config/menu/access';
import RemoteLink from '../../components/RemoteLink';
import HasPermissions from '../../components/HasPermissions';
import { logout } from '../../utils/api';
import { toggleAvailability } from '../../app/state';
import { clearSession } from '../../app/session';
import { isNativeWrapper, uiVersion, getAppVersion, getPlatform, sendCommand } from '../../utils/mobile';
import UserProfile from '../../components/UserProfile';
import Screen from '../../components/Screen';
import { localize } from '../../i18n';
import LanguageSwitcher from '../../containers/LanguageDropdown';
import { Features } from '../../utils';

import './Settings.css';
import { HistoryType } from '../../types/router';

class Settings extends Component {
  constructor(props) {
    super(props);

    this.state = {
      showLogoutPrompt: false,
    };
  }

  goToLocation(location) {
    this.props.history.push(location);
  }

  logout() {
    this.setState({ showLogoutPrompt: false });
    clearSession();

    sendCommand('app-delete').then(logout);
  }

  render() {
    const {
      showLogging,
      isAvailable,
      helpUrl,
      mailto,
      supportUrl,
      singleAffiliateUser,
      dispatch,
    } = this.props;

    return (
      <Screen title={this.props.t('Settings')} helpUrl="#settings">
        <div className="settings-page">
          <Dialog
            modal
            title={this.props.t('Change availability')}
            actions={[
              <FlatButton
                key="cancel"
                label={this.props.t('Cancel')}
                onClick={() => this.setState({ showLogoutPrompt: false })}
              />,
              <FlatButton
                key="no"
                label={this.props.t('No')}
                onClick={() => this.logout()}
              />,
              <FlatButton
                primary
                key="yes"
                label={this.props.t('Yes')}
                onClick={() => dispatch(toggleAvailability()).then(() => this.logout())}
              />,
            ]}
            open={this.state.showLogoutPrompt}
          >
            {this.props.t('Would you like to set your status to unavailable before logging off?')}
          </Dialog>
          <UserProfile showUsername />
          <List>
            <HasPermissions config="enable_staff_availability">
              <ListItem leftIcon={<ActionAccountBox />}>
                <Toggle
                  defaultToggled={isAvailable}
                  label={this.props.t('On/Off Duty')}
                  onToggle={() => dispatch(toggleAvailability())}
                  className="staff-toggle"
                />
              </ListItem>
            </HasPermissions>

            { !singleAffiliateUser ? <ListItem
              primaryText={this.props.t('Change Affiliate')}
              leftIcon={<ActionHome />}
              onClick={() => this.goToLocation('/affiliates')}
            /> : null }

            <RemoteLink href={helpUrl} target="_blank" rel="noopener noreferrer">
              <ListItem leftIcon={<ActionHelp />}>
                {this.props.t('Help')}
              </ListItem>
            </RemoteLink>
            <ListItem
              primaryText={this.props.t('About')}
              leftIcon={<ActionInfo />}
              onClick={() => this.goToLocation('/access/settings/about')}
            />
            <RemoteLink href={supportUrl} target="_blank" rel="noopener noreferrer">
              <ListItem leftIcon={<CommunicationLiveHelp />}>
                {this.props.t('Support')}
              </ListItem>
            </RemoteLink>
            <a href={mailto}>
              <ListItem leftIcon={<CommunicationEmail />}>
                {this.props.t('Send feedback')}
              </ListItem>
            </a>
            <Features has="localization">
              <ListItem className="language-switcher-menu" leftIcon={<ActionLanguage />}>
                <LanguageSwitcher />
              </ListItem>
            </Features>
            <ListItem
              primaryText={this.props.t('Logout')}
              leftIcon={<ActionExitToApp />}
              onClick={() => {
                if (isAvailable) {
                  return this.setState({ showLogoutPrompt: true });
                }
                this.logout();
                return true;
              }}
            />
            {showLogging ? (
              <ListItem
                primaryText={this.props.t('Logging')}
                leftIcon={<ActionBugReport />}
                onClick={() => this.goToLocation('/access/settings/logging')}
              />
            ) : null}
          </List>
        </div>
      </Screen>
    );
  }
}

Settings.propTypes = {
  showLogging: PropTypes.bool,
  isAvailable: PropTypes.bool,
  singleAffiliateUser: PropTypes.bool,
  helpUrl: PropTypes.string,
  mailto: PropTypes.string,
  supportUrl: PropTypes.string,
  dispatch: PropTypes.func,
  history: HistoryType,
  t: PropTypes.func.isRequired,
};

const mapStateToProps = (state) => {
  const {
    session: {
      affiliate,
      permissions: {
        user: userPermission = {},
      },
      user,
      singleAffiliateUser,
    },
  } = state;

  const supportUrl = 'https://support.intelity.com/';

  const helpMenuItem = menuAccess.find(item => item.key === 'help');
  const helpUrl = helpMenuItem.helpUrl;

  const showLogging =
    (_.get(user, 'attributes.username', '').indexOf('@keypr.com') !== -1 ||
     _.get(user, 'attributes.username', '').indexOf('@intelity.com') !== -1 ||
     _.get(user, 'attributes.username', '').indexOf('@intelitycorp.com') !== -1) && isNativeWrapper();

  const affiliateName = affiliate.attributes.name;
  const encodedBody = encodeURIComponent(`Affiliate name: ${affiliateName}\nApp name: GEMS Access\nApp version: GEMS v${uiVersion} ${getAppVersion()}\nOS version: ${navigator.userAgent}`);
  const encodedSubject = encodeURIComponent(`GEMS Access ${getPlatform()}`);
  const mailto = `mailto:Feedback-GEMS-Access@keypr.com?subject=${encodedSubject}&body=${encodedBody}`;

  return {
    helpUrl,
    user,
    showLogging,
    isAvailable: _.get(userPermission, 'attributes.is_available', false),
    userPermissionId: userPermission.id,
    singleAffiliateUser,
    mailto,
    supportUrl,
  };
};

export default localize(connect(mapStateToProps)(Settings));



// WEBPACK FOOTER //
// ./src/views/settings/Settings.js